import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGraduationCap, faPlay } from '@/fontawesome';
import type { DashboardVideoProgress } from '@/modelsAPI';

interface AcademyProgressCardProps {
  lastViewedVideos: DashboardVideoProgress[];
  className?: string;
}

export default function AcademyProgressCard({ lastViewedVideos = [], className = '' }: AcademyProgressCardProps) {
  const lastWatched = lastViewedVideos.length > 0 ? lastViewedVideos[0] : null;

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md border border-gray-200 dark:border-gray-700 h-[320px] flex flex-col ${className}`}>
      <div className="flex items-center space-x-2 mb-4">
        <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/50 rounded-lg flex items-center justify-center">
          <FontAwesomeIcon icon={faGraduationCap} className="w-4 h-4 text-purple-600 dark:text-purple-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Weiterschauen
        </h3>
      </div>

      {/* Course Info */}
      <div className="flex-1 flex flex-col justify-center">
        {lastWatched === null ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Noch keine Videos angesehen
            </p>
            <Link
              to="/videos"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors cursor-pointer"
            >
              <FontAwesomeIcon icon={faGraduationCap} className="w-4 h-4" />
              <span>Zur Academy</span>
            </Link>
          </div>
        ) : (
          <>
            {/* Course Title */}
            <div className="mb-4">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Kurs</p>
              <p className="text-base font-bold text-gray-900 dark:text-white truncate">
                {lastWatched.courseTitle}
              </p>
            </div>

            {/* Module */}
            <div className="mb-2">
              <p className="text-sm text-gray-700 dark:text-gray-300 truncate">
                {lastWatched.moduleTitle}
              </p>
            </div>

            {/* Video */}
            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                {lastWatched.videoTitle}
              </p>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">Fortschritt</span>
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                  {Math.round(lastWatched.progress)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-purple-600 dark:bg-purple-500 h-2 rounded-full transition-all"
                  style={{ width: `${lastWatched.progress}%` }}
                />
              </div>
            </div>

            {/* Continue Button */}
            <Link
              to={`/courses/${lastWatched.courseID}/modules/${lastWatched.moduleID}/videos/${lastWatched.videoID}`}
              className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 cursor-pointer"
            >
              <FontAwesomeIcon icon={faPlay} className="w-4 h-4" />
              <span>Fortsetzen</span>
            </Link>
          </>
        )}
      </div>
    </div>
  )
}
